import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import api from '../../services/api';
import '../../styles/BasicInfoTab.css';

const BasicInfoTab = ({ formData, masterData, errors, onChange, isGeneratingCode }) => {
    const jobTypes = masterData.jobTypes || [];
    const customers = masterData.customers || [];
    const employees = masterData.employees || [];
    const vessels = masterData.vessels || [];
    const airlines = masterData.airlines || [];
    const ports = masterData.ports || [];
    const countries = masterData.countries || [];

    // Sub-job state
    const [isSubJob, setIsSubJob] = useState(false);
    const [mainJobs, setMainJobs] = useState([]);
    const [selectedMainJob, setSelectedMainJob] = useState(null);
    const [mainJobInfo, setMainJobInfo] = useState(null);
    const [loadingMainJobs, setLoadingMainJobs] = useState(false);

    // Initialize sub-job state from formData
    useEffect(() => {
        if (formData.MainJobID && formData.MainJobID > 0) {
            setIsSubJob(true);
            setSelectedMainJob(formData.MainJobID);
        }
    }, [formData.MainJobID]);

    // Load main jobs when checkbox is checked
    const loadMainJobs = async () => {
        try {
            setLoadingMainJobs(true);
            console.log('📥 Loading main jobs...');
            const response = await api.get('/JobGeneration.php/main-jobs');
            if (response.data.success) {
                setMainJobs(response.data.data.mainJobs);
                console.log('✅ Main jobs loaded:', response.data.data.mainJobs.length);
            }
        } catch (err) {
            console.error('❌ Error loading main jobs:', err);
            alert('Failed to load main jobs: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoadingMainJobs(false);
        }
    };

    // Handle sub-job checkbox toggle
    const handleSubJobToggle = async (checked) => {
        setIsSubJob(checked);

        if (checked) {
            // CHECKED: Load main jobs and set StatusSub
            await loadMainJobs();
            onChange('StatusSub', true);
            onChange('MainJobID', null);
        } else {
            // UNCHECKED: Clear sub-job data
            setSelectedMainJob(null);
            setMainJobInfo(null);
            onChange('StatusSub', false);
            onChange('MainJobID', null);
            
            // Clear pre-filled fields
            onChange('ConsignerID', null);
            onChange('ConsigneeID', null);
            onChange('InvoiceToID', null);
            onChange('ShipperID', null);
            onChange('EmployeeID', null);
            onChange('LoadPortID', null);
            onChange('DestinationPortID', null);
            onChange('VesselID', null);
            onChange('AirlineID', null);
            onChange('TransporterID', null);
            onChange('CarrierID', null);
            onChange('CountryofOriginID', null);
        }
    };

    // Handle main job selection
    const handleMainJobSelect = async (mainJobId) => {
        setSelectedMainJob(mainJobId);

        if (!mainJobId) {
            setMainJobInfo(null);
            onChange('MainJobID', null);
            return;
        }

        try {
            console.log('📥 Loading main job details:', mainJobId);
            const response = await api.get(`/JobGeneration.php/main-job-details/${mainJobId}`);
            
            if (response.data.success) {
                const data = response.data.data;
                setMainJobInfo(data);
                console.log('✅ Main job details loaded');

                // Pre-fill form fields
                onChange('MainJobID', parseInt(mainJobId));
                onChange('JobTypeID', data.JobTypeID);
                onChange('ConsignerID', data.ConsignerID);
                onChange('ConsigneeID', data.ConsigneeID);
                onChange('InvoiceToID', data.InvoiceToID);
                onChange('ShipperID', data.ShipperID);
                onChange('EmployeeID', data.EmployeeID);
                onChange('LoadPortID', data.LoadPortID);
                onChange('DestinationPortID', data.DestinationPortID);
                onChange('VesselID', data.VesselID);
                onChange('AirlineID', data.AirlineID);
                onChange('TransporterID', data.TransporterID);
                onChange('CarrierID', data.CarrierID);
                onChange('CountryofOriginID', data.CountryofOriginID);

                // Generate sub-job code (will be handled in parent component)
            }
        } catch (err) {
            console.error('❌ Error loading main job details:', err);
            alert('Failed to load main job details: ' + (err.response?.data?.message || err.message));
        }
    };

    const renderSelect = (field, label, options, required = false, helpText = null, disabled = false) => (
        <div className="form-group">
            <label>
                {label}
                {required && <span className="required">*</span>}
                {helpText && (
                    <span className="help-icon" title={helpText}>
                        <Info size={14} />
                    </span>
                )}
            </label>
            <select
                value={formData[field] || ''}
                onChange={(e) => onChange(field, e.target.value ? parseInt(e.target.value) : null)}
                className={errors[field] ? 'error' : ''}
                disabled={disabled}
            >
                <option value="">Select {label}</option>
                {options.map(opt => (
                    <option key={opt.id} value={opt.id}>
                        {opt.name} {opt.ReferenceCode ? `(${opt.ReferenceCode})` : ''}
                    </option>
                ))}
            </select>
            {errors[field] && <span className="error-message">{errors[field]}</span>}
        </div>
    );

    const renderInput = (field, label, type = 'text', required = false) => (
        <div className="form-group">
            <label>
                {label}
                {required && <span className="required">*</span>}
            </label>
            <input
                type={type}
                value={formData[field] || ''}
                onChange={(e) => onChange(field, e.target.value)}
                className={errors[field] ? 'error' : ''}
                readOnly={field === 'JobCode'}
            />
            {errors[field] && <span className="error-message">{errors[field]}</span>}
        </div>
    );

    const isSeaJob = jobTypes.find(jt => jt.id === formData.JobTypeID)?.StatusSea === 1;

    return (
        <div className="basic-info-tab">
            <div className="form-section">
                <h3>Job Information</h3>
                <div className="form-row">
                    {renderSelect('JobTypeID', 'Job Type', jobTypes, true, null, isSubJob)}
                    {renderInput('JobCode', 'Job Code', 'text', true)}
                    {renderInput('JobDate', 'Job Date', 'date', true)}
                </div>
                
                {/* SUB-JOB SECTION */}
                <div className="form-row">
                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={isSubJob}
                                onChange={(e) => handleSubJobToggle(e.target.checked)}
                            />
                            <span>This is a Sub-Job</span>
                            {isSubJob && <span className="subjob-badge">SUB-JOB</span>}
                        </label>
                    </div>
                </div>

                {/* MAIN JOB SELECTOR (shown when sub-job is checked) */}
                {isSubJob && (
                    <div className="main-job-section">
                        <div className="form-group">
                            <label>
                                Select Main Job <span className="required">*</span>
                            </label>
                            <select
                                value={selectedMainJob || ''}
                                onChange={(e) => handleMainJobSelect(e.target.value)}
                                disabled={loadingMainJobs}
                                className={errors.MainJobID ? 'error' : ''}
                            >
                                <option value="">-- Select Main Job --</option>
                                {mainJobs.map(job => (
                                    <option key={job.id} value={job.id}>
                                        {job.displayText}
                                    </option>
                                ))}
                            </select>
                            {errors.MainJobID && <span className="error-message">{errors.MainJobID}</span>}
                            {loadingMainJobs && <small className="loading-text">Loading main jobs...</small>}
                        </div>

                        {/* MAIN JOB INFO DISPLAY */}
                        {mainJobInfo && (
                            <div className="main-job-info">
                                <h4>Main Job Information</h4>
                                <div className="info-row">
                                    <span className="info-label">Job Code:</span>
                                    <span className="info-value">{mainJobInfo.MainJobCode}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Job Type:</span>
                                    <span className="info-value">{mainJobInfo.JobDescription}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Customer:</span>
                                    <span className="info-value">{mainJobInfo.InvoiceToName}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {isGeneratingCode && (
                    <div className="info-box info-box-warning">
                        <Info size={16} />
                        <span>Generating job code...</span>
                    </div>
                )}
            </div>

            <div className="form-section">
                <h3>Customer Information</h3>
                
                {/* Invoice To - This will trigger auto-fill */}
                <div className="form-row">
                    {renderSelect(
                        'InvoiceToID', 
                        'Invoice To', 
                        customers, 
                        true,
                        'Selecting this will auto-fill Consigner and Consignee with the same customer'
                    )}
                </div>
                
                {/* Auto-filled fields with indicator */}
                <div className="form-row">
                    <div className="form-group">
                        <label>
                            Consigner
                            {formData.ConsignerID === formData.InvoiceToID && formData.ConsignerID && (
                                <span className="auto-filled-badge" title="Auto-filled from Invoice To">
                                    ✓ Auto
                                </span>
                            )}
                        </label>
                        <select
                            value={formData.ConsignerID || ''}
                            onChange={(e) => onChange('ConsignerID', e.target.value ? parseInt(e.target.value) : null)}
                        >
                            <option value="">Select Consigner</option>
                            {customers.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.name} {opt.ReferenceCode ? `(${opt.ReferenceCode})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="form-group">
                        <label>
                            Consignee
                            {formData.ConsigneeID === formData.InvoiceToID && formData.ConsigneeID && (
                                <span className="auto-filled-badge" title="Auto-filled from Invoice To">
                                    ✓ Auto
                                </span>
                            )}
                        </label>
                        <select
                            value={formData.ConsigneeID || ''}
                            onChange={(e) => onChange('ConsigneeID', e.target.value ? parseInt(e.target.value) : null)}
                        >
                            <option value="">Select Consignee</option>
                            {customers.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.name} {opt.ReferenceCode ? `(${opt.ReferenceCode})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                
                {/* Info box about auto-fill feature */}
                {formData.InvoiceToID && !isSubJob && (
                    <div className="info-box">
                        <Info size={16} />
                        <span>
                            Customer auto-filled: When you select "Invoice To", 
                            both Consigner and Consignee are automatically set to the same customer. 
                            You can change them individually if needed.
                        </span>
                    </div>
                )}

                {/* Info box for sub-job pre-fill */}
                {isSubJob && mainJobInfo && (
                    <div className="info-box info-box-success">
                        <Info size={16} />
                        <span>
                            Customer information has been pre-filled from main job: {mainJobInfo.MainJobCode}. 
                            You can modify these values if needed.
                        </span>
                    </div>
                )}
                
                <div className="form-row">
                    {renderSelect('ShipperID', 'Shipper', customers)}
                    {renderSelect('EmployeeID', 'Employee', employees)}
                </div>
                <div className="form-row">
                    {renderInput('CustRef', 'Customer Reference')}
                </div>
            </div>

            <div className="form-section">
                <h3>Shipping Details</h3>
                
                {isSeaJob ? (
                    <>
                        <div className="form-row">
                            {renderSelect('VesselID', 'Vessel', vessels)}
                            {renderInput('VoyageNo', 'Voyage No')}
                        </div>
                        <div className="form-row">
                            {renderInput('MBL', 'Master B/L')}
                            {renderInput('HBL', 'House B/L')}
                        </div>
                        <div className="form-row">
                            {renderInput('BLDate', 'B/L Date', 'date')}
                            {renderInput('SailingDate', 'Sailing Date', 'date')}
                            {renderInput('ArrivalDate', 'Arrival Date', 'date')}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="form-row">
                            {renderSelect('AirlineID', 'Airline', airlines)}
                            {renderInput('Flight', 'Flight No')}
                        </div>
                        <div className="form-row">
                            {renderInput('MAWB', 'Master AWB')}
                            {renderInput('HAWB', 'House AWB')}
                        </div>
                        <div className="form-row">
                            {renderInput('ArrivalDate', 'Arrival Date', 'date')}
                        </div>
                    </>
                )}
            </div>

            <div className="form-section">
                <h3>Port Information</h3>
                <div className="form-row">
                    {renderSelect('LoadPortID', 'Load Port', ports)}
                    {renderSelect('DestinationPortID', 'Destination Port', ports)}
                </div>
                <div className="form-row">
                    {renderSelect('ReceiptPlaceID', 'Receipt Place', ports)}
                    {renderSelect('DeliveryPlaceID', 'Delivery Place', ports)}
                </div>
                <div className="form-row">
                    {renderSelect('CountryofOriginID', 'Country of Origin', countries)}
                </div>
            </div>

            <div className="form-section">
                <h3>Cargo Information</h3>
                <div className="form-row">
                    {renderInput('GrossWeight', 'Gross Weight', 'number')}
                    {renderInput('Volume', 'Volume (CBM)', 'number')}
                    {renderInput('Package', 'Package')}
                </div>
            </div>

            <div className="form-section">
                <h3>References</h3>
                <div className="form-row">
                    {renderInput('IPTNo', 'IPT No')}
                    {renderInput('RefNo', 'Reference No')}
                </div>
                <div className="form-row">
                    {renderInput('DeliveryOrderNo', 'Delivery Order No')}
                    {renderInput('CollectionPoint', 'Collection Point')}
                </div>
                <div className="form-row">
                    {renderInput('MRN', 'MRN')}
                    {renderInput('RotationNo', 'Rotation No')}
                </div>
            </div>

            <div className="form-section">
                <h3>Financial Information</h3>
                <div className="form-row">
                    <div className="form-group">
                        <label>Invoice Type</label>
                        <select
                            value={formData.StausInvoiceType || 'CR'}
                            onChange={(e) => onChange('StausInvoiceType', e.target.value)}
                        >
                            <option value="CR">Credit</option>
                            <option value="CS">Cash</option>
                        </select>
                    </div>
                    {renderInput('Freight', 'Freight')}
                    {renderInput('BLStatus', 'B/L Status')}
                </div>
                <div className="form-row">
                    {renderInput('CLFValue', 'CLF Value', 'number')}
                    {renderInput('DepositAmount', 'Deposit Amount', 'number')}
                    {renderInput('RefundAmount', 'Refund Amount', 'number')}
                </div>
            </div>

            <div className="form-section">
                <h3>Instructions & Remarks</h3>
                <div className="form-group full-width">
                    <label>Shipping Instructions</label>
                    <textarea
                        value={formData.ShippingInstruction || ''}
                        onChange={(e) => onChange('ShippingInstruction', e.target.value)}
                        rows="3"
                    />
                </div>
                <div className="form-group full-width">
                    <label>Delivery Instructions</label>
                    <textarea
                        value={formData.DeliveryInstructions || ''}
                        onChange={(e) => onChange('DeliveryInstructions', e.target.value)}
                        rows="3"
                    />
                </div>
                <div className="form-group full-width">
                    <label>Collection Instructions</label>
                    <textarea
                        value={formData.CollectionInstructions || ''}
                        onChange={(e) => onChange('CollectionInstructions', e.target.value)}
                        rows="3"
                    />
                </div>
                <div className="form-group full-width">
                    <label>Remarks</label>
                    <textarea
                        value={formData.Remarks || ''}
                        onChange={(e) => onChange('Remarks', e.target.value)}
                        rows="4"
                    />
                </div>
            </div>
        </div>
    );
};

export default BasicInfoTab;